// @ts-strict-ignore
import {
    calculateStatistics,
    categoryForAutomation,
    compareVersions,
    extendedStatTypeForReleaseStatistic,
    getSortedCommitsOnlyOnWorkingBranch,
    getSortedOpenPRs,
    groupReleasesByTimelinePhase,
    hasEffectiveAppAccess,
    isDraftForCodeItem,
    statusChangeForStatType,
} from './../helpers';
import {
    Automation,
    AutomationType,
    CommitItem,
    DefaultUserGroupId,
    ExtendedStatisticType,
    FeatureItemDebugVariant,
    LiteOrgUser,
    mapCodeStatus,
    PRItem,
    PullRequestMergeQueueState,
    ResolvedFeatureAffiliation,
    Statistic,
    StatisticCategory,
    StatisticType,
    StatisticUnit,
    StatisticWithAssociatedInfo,
    User,
    UserGroup,
    UserGroupId,
} from './../types';
import { compact, concat, Dictionary, forEach, groupBy, isNil, keyBy, keys, reduce, unionBy } from 'lodash';
import { createSelector } from 'reselect';
import {
    AssociatedStabilityIssue,
    ChecklistItem,
    FeatureFlag,
    FeatureItem,
    StabilityIssue,
    TicketProject,
} from '../types';
import {
    AnyFilter,
    AssociatedIssuesFilter,
    CodeAuthorFilter,
    CodeFAFilter,
    CodeStatusFilter,
    FilterType,
    FirstSeenInRCBuildFilter,
    HideSubtasksFilter,
    HideUnsquashedChildCommitsFilter,
    HideDraftPRsFilter,
    OnlyShowFixesOnly,
    OnlyShowItemsWithFAsFilter,
    OnlyShowItemsWithoutTickets,
    TicketCustomFieldFilter,
    ticketCustomFieldNameFromFilter,
    TicketFAFilter,
    TicketOwnerFilter,
    TicketPriorityFilter,
    TicketProjectFilter,
    TicketStatusFilter,
    TicketTypeFilter,
} from '../types/filters';
import {
    ApprovalsSortType,
    AutomationsToolboxSortType,
    FeatureFlagsSortType,
    groupFeatureItemsByStack,
    sortAppsByPlatform,
    sortFeatureFlagsByStatus,
    sortFunctionForSortType,
    sortFunctionsForApprovalsSortTypes,
    SortType,
} from '../types/sorts';
import {
    selectAllFeatureWorkItems,
    selectAllOpenPRsAgainstReleaseBranch,
    selectAllOpenPRsAgainstWorkingBranch,
    selectAllReleaseBranchWorkItems,
    selectAllWorkingBranchWorkItems,
    selectApp,
    selectAppHome,
    selectAppIntegrations,
    selectApprovalItems,
    selectApprovalsSearch,
    selectApprovalsSort,
    selectAppsById,
    selectAppSettingAutomations,
    selectAppsInAppGroup,
    selectAutomationsToolboxSort,
    selectFeatureFlags,
    selectFeatureFlagsSort,
    selectFeatureWorkItems,
    selectFeatureWorkItemsForAppIdReleaseId,
    selectFilters,
    selectGroupMembers,
    selectHasFeatureManagementIntegration,
    selectHasFeatureWorkItemsForStepRunId,
    selectOrg,
    selectOrgGroups,
    selectOrgUsers,
    selectRelease,
    selectReleasesForAppId,
    selectReleaseStatsHistory,
    selectSearch,
    selectShouldGroupWorkItemsByTicket,
    selectShouldShowFixesOnly,
    selectSort,
    selectUser,
    selectUsersSearch,
} from './selectors';
import { combinedStatusForIntegrationRefreshData } from '@/helpers/integrationRefreshDataHelpers';
import { featureAffiliationsFromCode, isCommit } from '@/components/featureReadiness/featureReadinessHelpers';
import { allCases, StringKeyedDictionary } from '../typescriptHelpers';
import { RootState } from '@/reducers/reducers';

/* A map of currently applied filter types and an array of their values, 
since some filters can be applied multiple times with different values.
 */
export const selectFiltersMap = createSelector(selectFilters, (filters) => {
    return groupBy(filters, (filter) => filter.type);
});

export const selectFilteredWorkItemsForStepRun = createSelector(
    [
        selectFeatureWorkItems,
        selectFiltersMap,
        selectShouldShowFixesOnly,
        selectShouldGroupWorkItemsByTicket,
        selectUser,
    ],
    (
        items: FeatureItem[],
        filtersMap: Dictionary<AnyFilter[]>,
        showFixesOnly: boolean,
        groupItemsByTicket: boolean,
        user: User,
    ) => {
        const vcsUsers = Object.values(user?.vcsUserDataByProvider ?? {}).map((vcsUserData) => vcsUserData.username);
        const filteredItems = items.filter((item) => filterItem(item, filtersMap, showFixesOnly, vcsUsers));

        if (!groupItemsByTicket) {
            return filteredItems;
        }

        const finalItems: FeatureItem[] = [];
        const doneItemsByTicketKey: { [key: string]: FeatureItem[] } = {};
        const pendingItemsByTicketKey: { [key: string]: FeatureItem[] } = {};

        filteredItems.forEach((item) => {
            // items could be grouped if they have a ticket and no fix req
            if (item.ticket && !item.fixRequest) {
                const key = item.ticket.identifier;
                const dict = item.isItemDone ? doneItemsByTicketKey : pendingItemsByTicketKey;

                if (dict[key] === undefined) {
                    dict[key] = [];
                }
                dict[key].push(item);
            } else {
                finalItems.push(item);
            }
        });

        const finalItemForItems = (items) => {
            if (items.length == 1) {
                return items[0];
            } else {
                const mergedItem: FeatureItem = {
                    id: items.reduce((result, current) => {
                        return result + current.id.substring(5);
                    }, ''),
                    ticket: items[0].ticket,
                    code: null,
                    explanationText: `This ticket has ${items.length} code items associated with it. For details on each item, remove the "Group by ticket" lens.`,
                    ignored: items.reduce((result, current) => {
                        return result && current.ignored;
                    }, true),
                    autoIgnored: items.reduce((result, current) => {
                        return result && current.autoIgnored;
                    }, true),
                    isItemDone: items[0].isItemDone,
                    isInLatestOrSelectedStagingBuild: items.reduce((result, current) => {
                        return result && current.isInLatestOrSelectedStagingBuild;
                    }, true),
                    isInLatestWorkingBranchBuild: items.reduce((result, current) => {
                        return result && current.isInLatestWorkingBranchBuild;
                    }, true),
                    debugVariant: FeatureItemDebugVariant.GroupedByTicket,
                    associatedStabilityIssues: items.reduce((result, current) => {
                        return concat(result, current.associatedStabilityIssues ?? []);
                    }, []),
                    fixRequest: null,
                    groupedCodeItems: items
                        .filter((item) => {
                            return !!item.code;
                        })
                        .sort(sortFunctionForSortType(SortType.CodeLastUpdated))
                        .reverse(),
                };

                return mergedItem;
            }
        };

        Object.values(pendingItemsByTicketKey).forEach((items) => {
            finalItems.push(finalItemForItems(items));
        });

        Object.values(doneItemsByTicketKey).forEach((items) => {
            finalItems.push(finalItemForItems(items));
        });

        return finalItems;
    },
);

export const selectFilteredSortedPendingWorkItemsForStepRun = createSelector(
    [selectFilteredWorkItemsForStepRun, selectSort, selectSearch],
    (items: FeatureItem[], sortType: SortType, query?: string) => {
        const filteredItems = items.filter((item) => !item.isItemDone && filterItemWithSearchQuery(item, query));
        const sortedItems = filteredItems.sort(sortFunctionForSortType(sortType));
        const sortedItemsByIsIgnored = sortedItems.sort((a, b) => {
            return Number(a.ignored) - Number(b.ignored);
        });
        return groupFeatureItemsByStack(sortedItemsByIsIgnored);
    },
);

export const selectFilteredSortedDoneItemsForStepRun = createSelector(
    [selectFilteredWorkItemsForStepRun, selectSort, selectSearch],
    (items: FeatureItem[], sortType: SortType, query?: string) => {
        const filteredItems = items.filter((item) => item.isItemDone && filterItemWithSearchQuery(item, query));
        const sortedItems = filteredItems.sort(sortFunctionForSortType(sortType));
        const sortedItemsByIsIgnored = sortedItems.sort((a, b) => {
            return Number(a.ignored) - Number(b.ignored);
        });

        return groupFeatureItemsByStack(sortedItemsByIsIgnored);
    },
);

export const selectAppliedFilters = createSelector(selectFilters, (filters) => {
    const appliedFilters = filters.filter((filter) => {
        switch (filter.type) {
            case FilterType.HideSubtasks: {
                if ((filter as HideSubtasksFilter).hideSubtasks) {
                    return true;
                }

                return false;
            }
            case FilterType.HideUnsquashedChildCommits: {
                if ((filter as HideUnsquashedChildCommitsFilter).hideUnsquashedChildCommits) {
                    return true;
                }

                return false;
            }
            case FilterType.HideDraftPRs: {
                if ((filter as HideDraftPRsFilter).hideDraftPRs) {
                    return true;
                }

                return false;
            }
            case FilterType.HideItemsWithMissingFAs: {
                if ((filter as OnlyShowItemsWithFAsFilter).hideMissingFeatureAffiliations) {
                    return true;
                }

                return false;
            }
            case FilterType.HideItemsWithTickets: {
                if ((filter as OnlyShowItemsWithoutTickets).hideItemsWithTickets) {
                    return true;
                }

                return false;
            }
            case FilterType.ShowFixesOnly: {
                if ((filter as OnlyShowFixesOnly).showFixesOnly) {
                    return true;
                }

                return false;
            }
            case FilterType.TicketOwner:
            case FilterType.CodeAuthor:
            case FilterType.CodeStatus:
            case FilterType.CodeFA:
            case FilterType.TicketProject:
            case FilterType.TicketStatus:
            case FilterType.TicketPriority:
            case FilterType.TicketType:
            case FilterType.TicketFA:
            case FilterType.AssociatedIssues:
            case FilterType.FirstSeenInRCBuild:
            case FilterType.TicketCustomField:
                return true;
        }
    });

    return appliedFilters;
});

export const selectAppliedWorkItemFiltersCount = createSelector(selectAppliedFilters, (filters) => {
    const filtersKeyedByType = keyBy(filters, (filter) =>
        filter.type == FilterType.TicketCustomField ? (filter as TicketCustomFieldFilter).key : filter.type,
    );

    return Object.keys(filtersKeyedByType).length;
});

export const selectCompletedWorkItems = createSelector(
    selectFeatureWorkItemsForAppIdReleaseId,
    selectHasFeatureManagementIntegration,
    (items: FeatureItem[], hasFeatureManagementIntegration: boolean) => {
        return items.filter((item) => {
            if (item.ignored) {
                return false;
            } else {
                if (hasFeatureManagementIntegration) {
                    return !!item.ticket?.identifier && item.isItemDone;
                } else {
                    // if no FR integration connected, we'll also show completed code items
                    return item.isItemDone;
                }
            }
        });
    },
);

export const selectTicketOwners = createSelector(selectAllFeatureWorkItems, (items) => {
    return reduce(
        items,
        (ticketOwners, item) => {
            if (item.ticket?.owner) {
                // only add owner if it's not already present
                if (!ticketOwners.includes(item.ticket.owner)) {
                    ticketOwners.push(item.ticket.owner);

                    return ticketOwners;
                }
            }

            return ticketOwners;
        },
        [],
    );
});

export const selectTicketFAs = createSelector(selectAllFeatureWorkItems, (items) => {
    return reduce(
        items,
        (ticketFAs, item) => {
            item.ticket?.featureAffiliations?.forEach((fa) => {
                if (!ticketFAs.includes(fa.resolvedString)) {
                    ticketFAs.push(fa.resolvedString);
                }
            });
            return ticketFAs;
        },
        [],
    );
});

export const selectCodeFAs = createSelector(selectAllFeatureWorkItems, (items) => {
    return reduce(
        items,
        (codeFAs, item) => {
            if (item.code) {
                featureAffiliationsFromCode(item.code).forEach((fa) => {
                    if (!codeFAs.includes(fa.resolvedString)) {
                        codeFAs.push(fa.resolvedString);
                    }
                });
            }
            return codeFAs;
        },
        [],
    );
});

export const selectCodeAuthors = createSelector(selectAllFeatureWorkItems, (items) => {
    return reduce(
        items,
        (codeAuthors, item) => {
            if (item.code?.author) {
                // only add owner if it's not already present
                if (!codeAuthors.includes(item.code.author)) {
                    codeAuthors.push(item.code.author);
                }
            }

            return codeAuthors;
        },
        [],
    );
});

export const selectTicketProjects = createSelector(selectAllFeatureWorkItems, (items) => {
    const projects = reduce(
        items,
        (allProjects: TicketProject[], item) => {
            if (item.ticket?.project) {
                if (!allProjects.find((project) => project.identifier === item.ticket.project.identifier)) {
                    allProjects.push(item.ticket.project);
                }
            }

            return allProjects;
        },
        [],
    );

    return projects.sort();
});

export const selectTicketStatuses = createSelector(selectAllFeatureWorkItems, (items) => {
    const statuses = reduce(
        items,
        (allTicketStatuses: string[], item) => {
            if (item.ticket?.status) {
                if (!allTicketStatuses.includes(item.ticket.status)) {
                    allTicketStatuses.push(item.ticket.status);
                }
            }

            return allTicketStatuses;
        },
        [],
    );

    return statuses.sort();
});

export const selectTicketPriorities = createSelector(selectAllFeatureWorkItems, (items) => {
    const priorities = reduce(
        items,
        (allTicketPriorities: string[], item) => {
            if (item.ticket?.priority) {
                if (!allTicketPriorities.includes(item.ticket.priority)) {
                    allTicketPriorities.push(item.ticket.priority);
                }
            }

            return allTicketPriorities;
        },
        [],
    );

    return priorities.sort();
});

export const selectTicketTypes = createSelector(selectAllFeatureWorkItems, (items) => {
    const types = reduce(
        items,
        (allTicketTypes: string[], item) => {
            if (item.ticket?.ticketType) {
                if (!allTicketTypes.includes(item.ticket.ticketType)) {
                    allTicketTypes.push(item.ticket.ticketType);
                }
            }

            return allTicketTypes;
        },
        [],
    );

    return types.sort();
});

export const selectTicketFormFieldValues = createSelector(selectAllFeatureWorkItems, (items) => {
    let formFieldValues: StringKeyedDictionary<string[]> = {};
    forEach(items, (item) => {
        if (item.ticket) {
            forEach(item.ticket.additionalFields, (v, k) => {
                if (!formFieldValues[k]) {
                    formFieldValues[k] = [];
                }
                forEach(v, (val) => {
                    if (!formFieldValues[k].includes(val)) {
                        formFieldValues[k].push(val);
                    }
                });
            });
        }
    });

    return formFieldValues;
});

export const selectFirstSeenInStagingBuildValues = createSelector(selectAllFeatureWorkItems, (items) => {
    const buildIdentifiers = reduce(
        items,
        (buildIds: string[], item) => {
            if (item.firstSeenInStagingBuild && !buildIds.includes(item.firstSeenInStagingBuild)) {
                buildIds.push(item.firstSeenInStagingBuild);
            }
            return buildIds;
        },
        [],
    );
    return buildIdentifiers.sort((a, b) => {
        const aNum = parseInt(a, 10);
        const bNum = parseInt(b, 10);
        if (!isNaN(aNum) && !isNaN(bNum)) {
            return bNum - aNum;
        }
        return a.localeCompare(b);
    });
});

export const selectAssociatedIssues = createSelector(selectAllFeatureWorkItems, (items) => {
    const issues = reduce(
        items,
        (allAssociatedIssues: AssociatedStabilityIssue[], item) => {
            if (item.associatedStabilityIssues?.length > 0) {
                item.associatedStabilityIssues.forEach((issue1) => {
                    if (
                        !allAssociatedIssues.some((issue: AssociatedStabilityIssue) => {
                            if (issue.stabilityIssue.issueID === issue1.stabilityIssue.issueID) {
                                return true;
                            } else {
                                return false;
                            }
                        })
                    ) {
                        allAssociatedIssues.push(issue1);
                    }
                });
            }
            return allAssociatedIssues;
        },
        [],
    );
    return issues.sort();
});

export const MY_VCS_USERNAME_TICKET_OWNER = '#MY_VCS_USERNAME#';

const filterItem: (
    item: FeatureItem,
    filtersMap: Dictionary<AnyFilter[]>,
    shouldShowFixesOnly: boolean,
    vcsUsers: string[],
) => boolean = (item, filtersMap, shouldShowFixesOnly, vcsUsers) => {
    const includeItem = reduce(
        keys(filtersMap),
        (includeItem, filterType) => {
            if (includeItem) {
                switch (filterType) {
                    case FilterType.HideSubtasks: {
                        const filter = filtersMap[filterType][0];
                        if ((filter as HideSubtasksFilter).hideSubtasks) {
                            return !item.ticket?.isTicketSubtask;
                        }

                        return true;
                    }
                    case FilterType.HideUnsquashedChildCommits: {
                        const filter = filtersMap[filterType][0];
                        if ((filter as HideUnsquashedChildCommitsFilter).hideUnsquashedChildCommits) {
                            return !isCommit(item.code) || (item.code as CommitItem).unsquashedChildCommitForPR == null;
                        }

                        return true;
                    }
                    case FilterType.HideDraftPRs: {
                        const filter = filtersMap[filterType][0];
                        if ((filter as HideDraftPRsFilter).hideDraftPRs) {
                            return !isDraftForCodeItem(item.code);
                        }

                        return true;
                    }
                    case FilterType.HideItemsWithMissingFAs: {
                        const filter = filtersMap[filterType][0];

                        if ((filter as OnlyShowItemsWithFAsFilter).hideMissingFeatureAffiliations) {
                            if (!item.ticket?.identifier) {
                                return false;
                            }

                            return !item.ticket?.isTicketMissingFeatureAffiliation;
                        }

                        return true;
                    }
                    case FilterType.HideItemsWithTickets: {
                        const filter = filtersMap[filterType][0];

                        if ((filter as OnlyShowItemsWithoutTickets).hideItemsWithTickets) {
                            if (item.ticket) {
                                return false;
                            }
                        }

                        return true;
                    }
                    case FilterType.TicketOwner: {
                        const filters = filtersMap[filterType];

                        if (item.ticket?.owner) {
                            const allTicketOwners = filters.map((filter) => (filter as TicketOwnerFilter).ticketOwner);
                            return allTicketOwners.includes(item.ticket.owner);
                        }

                        // filter out if no ticket owner
                        return false;
                    }
                    case FilterType.CodeAuthor: {
                        const filters = filtersMap[filterType];

                        let hasMyUsernameFilter = false;

                        let processedFilters = filters.filter((filter) => {
                            if ((filter as CodeAuthorFilter).codeAuthor === MY_VCS_USERNAME_TICKET_OWNER) {
                                hasMyUsernameFilter = true;
                                return false;
                            }

                            return true;
                        });

                        if (hasMyUsernameFilter) {
                            processedFilters = [
                                ...processedFilters,
                                ...vcsUsers.map(
                                    (username) =>
                                        ({
                                            type: FilterType.CodeAuthor,
                                            codeAuthor: username,
                                        }) as AnyFilter,
                                ),
                            ];
                        }

                        if (item.code?.author) {
                            const allCodeAuthors = processedFilters.map(
                                (filter) => (filter as CodeAuthorFilter).codeAuthor,
                            );
                            return allCodeAuthors.includes(item.code.author);
                        }

                        // filter out if no code author
                        return false;
                    }
                    case FilterType.CodeStatus: {
                        const filters = filtersMap[filterType];

                        if (item.code?.status) {
                            const allCodeStatuses = filters.map((filter) => (filter as CodeStatusFilter).codeStatus);

                            if (
                                !isCommit(item.code) &&
                                item.code.mergeQueueState === PullRequestMergeQueueState.Queued
                            ) {
                                return allCodeStatuses.includes(PullRequestMergeQueueState.Queued);
                            }

                            return allCodeStatuses.includes(mapCodeStatus(item.code.status));
                        }

                        // filter out if no code status
                        return false;
                    }
                    case FilterType.CodeFA: {
                        const filters = filtersMap[filterType];

                        if (item.code) {
                            const allCodeFAs = filters.map((filter) => (filter as CodeFAFilter).codeFA);
                            for (const codeFA of allCodeFAs) {
                                if (
                                    !featureAffiliationsFromCode(item.code)
                                        .map((fa) => (fa as ResolvedFeatureAffiliation).resolvedString)
                                        .includes(codeFA)
                                ) {
                                    return false;
                                }
                            }
                            return true;
                        }

                        // filter out if no code FAs
                        return false;
                    }
                    case FilterType.TicketProject: {
                        const filters = filtersMap[filterType];

                        if (item.ticket?.project) {
                            const allTicketProjects = filters.map(
                                (filter) => (filter as TicketProjectFilter).ticketProjectId,
                            );

                            return allTicketProjects.includes(item.ticket.project?.identifier);
                        }

                        // filter out if no code status
                        return false;
                    }
                    case FilterType.TicketStatus: {
                        const filters = filtersMap[filterType];

                        if (item.ticket?.status) {
                            const allFilteredTicketStatuses = filters.map(
                                (filter) => (filter as TicketStatusFilter).status,
                            );

                            return allFilteredTicketStatuses.includes(item.ticket.status);
                        }

                        // filter out if no ticket
                        return false;
                    }
                    case FilterType.TicketPriority: {
                        const filters = filtersMap[filterType];

                        if (item.ticket?.priority) {
                            const allFilteredTicketPriorities = filters.map(
                                (filter) => (filter as TicketPriorityFilter).priority,
                            );

                            return allFilteredTicketPriorities.includes(item.ticket.priority);
                        }

                        // filter out if no ticket
                        return false;
                    }
                    case FilterType.TicketType: {
                        const filters = filtersMap[filterType];

                        if (item.ticket?.ticketType) {
                            const allFilteredTicketTypes = filters.map(
                                (filter) => (filter as TicketTypeFilter).ticketType,
                            );

                            return allFilteredTicketTypes.includes(item.ticket.ticketType);
                        }

                        // filter out if no ticket
                        return false;
                    }
                    case FilterType.TicketFA: {
                        const filters = filtersMap[filterType];

                        if (item.ticket) {
                            const allTicketFAs = filters.map((filter) => (filter as TicketFAFilter).ticketFA);
                            for (const ticketFA of allTicketFAs) {
                                if (
                                    item.ticket?.featureAffiliations
                                        ?.map((fa) => (fa as ResolvedFeatureAffiliation).resolvedString)
                                        .includes(ticketFA) !== true
                                ) {
                                    return false;
                                }
                            }
                            return true;
                        }

                        // filter out if no ticket FAs
                        return false;
                    }
                    case FilterType.AssociatedIssues: {
                        const filters = filtersMap[filterType];

                        if (item.associatedStabilityIssues?.length > 0) {
                            const allAssociatedIssues = filters.map(
                                (filter) => (filter as AssociatedIssuesFilter).associatedIssueId,
                            );

                            const allItemIssues = item.associatedStabilityIssues.map(
                                (issue) => issue.stabilityIssue.issueID,
                            );

                            return allAssociatedIssues.some((issue) => allItemIssues.includes(issue));
                        } else {
                            return false;
                        }
                    }
                    case FilterType.FirstSeenInRCBuild: {
                        const filters = filtersMap[filterType];

                        if (item.firstSeenInStagingBuild) {
                            const selectedBuildIds = filters.map(
                                (filter) => (filter as FirstSeenInRCBuildFilter).buildIdentifier,
                            );
                            return selectedBuildIds.includes(item.firstSeenInStagingBuild);
                        }

                        return false;
                    }
                    case FilterType.TicketCustomField: {
                        const filters = filtersMap[filterType] as TicketCustomFieldFilter[];

                        if (item.ticket?.additionalFields) {
                            for (let filter of filters) {
                                let fieldName = ticketCustomFieldNameFromFilter(filter);
                                if (item.ticket.additionalFields[fieldName]) {
                                    for (let v of item.ticket.additionalFields[fieldName]) {
                                        if (v == filter.value) {
                                            return true;
                                        }
                                    }
                                }
                            }
                        }

                        return false;
                    }
                    default:
                        return true;
                }
            } else {
                return includeItem;
            }
        },
        true,
    );

    if (shouldShowFixesOnly) {
        return includeItem && item.fixRequest !== null;
    }

    return includeItem;
};

const filterItemWithSearchQuery: (item: FeatureItem, query?: string) => boolean = (item, query) => {
    if (!query || query.length === 0) {
        return true;
    }

    const searchQuery = query.toLowerCase();

    if (
        item.fixRequest?.formFieldsValues?.some((v) => v.value?.toLowerCase().includes(searchQuery)) ||
        item.ticket?.identifier?.toLowerCase().includes(searchQuery) ||
        item.ticket?.description?.toLowerCase().includes(searchQuery) ||
        item.code?.identifier.toString().includes(searchQuery) ||
        item.code?.sourcePR?.number.toString().includes(searchQuery)
    ) {
        return true;
    }

    const commitItem = item.code as CommitItem;
    if (commitItem?.message?.toLowerCase().includes(searchQuery)) {
        return true;
    }

    const prItem = item.code as PRItem;
    if (prItem?.title?.toLowerCase().includes(searchQuery)) {
        return true;
    }

    return false;
};

export const selectBuildsIssueStore = createSelector(selectAssociatedIssues, (items) => {
    const store = {};
    const allIssues = [...items];

    const uniqueBuilds = [...new Set(allIssues.map((issue) => issue.buildIdentifier))];
    uniqueBuilds.forEach((build) => (store[build] = []));

    allIssues.forEach((issue) => {
        store[issue.buildIdentifier].push(issue.stabilityIssue);
    });

    return store;
});

export const selectIssuesByBuild = createSelector(selectBuildsIssueStore, (store) => {
    return Object.entries<StabilityIssue[]>(store).map(([buildId, stabIssue]) => {
        const issueValues = stabIssue.map((issue) => {
            return { label: issue.crashShortDescription, value: issue.issueID };
        });
        return {
            label: `Build #${buildId}`,
            value: buildId,
            children: issueValues,
        };
    });
});

export const selectSortedAutomationsToolboxAutomations = createSelector(
    [selectAppSettingAutomations, selectAutomationsToolboxSort],
    (items: Automation[], sortType: AutomationsToolboxSortType) => {
        const filteredItems = items.filter((item) => !UNLISTED_AUTOMATIONS.includes(item.type));
        const defaultOrder = [...filteredItems];
        switch (sortType) {
            case AutomationsToolboxSortType.Category:
                return defaultOrder.sort((a, b) =>
                    categoryForAutomation(a.type) < categoryForAutomation(b.type) ? 1 : -1,
                );
            case AutomationsToolboxSortType.New:
                return defaultOrder.sort((a, b) =>
                    a.isNew === b.isNew ? 0
                    : a.isNew ? -1
                    : 1,
                );
            case AutomationsToolboxSortType.Enabled:
                return defaultOrder.sort((a, b) =>
                    a.enabled === b.enabled ? 0
                    : a.enabled ? -1
                    : 1,
                );
        }
    },
);

export const selectSortedFeatureFlags = createSelector(
    [selectFeatureFlags, selectFeatureFlagsSort],
    (items: FeatureFlag[], sortType: FeatureFlagsSortType) => {
        const defaultOrder = [...items];

        const highlightedSort = defaultOrder.sort((flag1, flag2) => {
            if (flag1.shouldHighlight == flag2.shouldHighlight) {
                return sortFeatureFlagsByStatus(flag1, flag2);
            } else if (flag1.shouldHighlight) {
                return -1;
            } else if (flag2.shouldHighlight) {
                return 1;
            }

            return 0;
        });

        switch (sortType) {
            case FeatureFlagsSortType.Highlighted:
                return highlightedSort;
            case FeatureFlagsSortType.Status:
                return highlightedSort.sort(sortFeatureFlagsByStatus);
            case FeatureFlagsSortType.CreatedOn:
                return highlightedSort.sort(
                    (flag1, flag2) =>
                        new Date(flag2.createdAt).getTime() - new Date(flag1.createdAt).getTime() ||
                        sortFeatureFlagsByStatus(flag1, flag2),
                );
            case FeatureFlagsSortType.Owner:
                return highlightedSort.sort((flag1, flag2) => {
                    // Flags without an owner sink to the bottom
                    if (!flag1.owner !== !flag2.owner) {
                        return flag1.owner ? -1 : 1;
                    }
                    return (
                        (flag1.owner ?? '').localeCompare(flag2.owner ?? '', undefined, { sensitivity: 'base' }) ||
                        sortFeatureFlagsByStatus(flag1, flag2)
                    );
                });
            case FeatureFlagsSortType.Name:
                return highlightedSort.sort((flag1, flag2) =>
                    flag1.name.localeCompare(flag2.name, undefined, { sensitivity: 'base' }),
                );
        }
    },
);

export const selectSortedApprovalsItems = createSelector(
    [selectApprovalItems, selectApprovalsSort],
    (items: ChecklistItem[], sortType: ApprovalsSortType) => {
        const sortedDefault = [...items];
        if (sortType === ApprovalsSortType.Default) {
            return sortedDefault;
        }
        return sortedDefault.sort(sortFunctionsForApprovalsSortTypes(sortType));
    },
);

export const selectSortedSearchedApprovalsItems = createSelector(
    [selectSortedApprovalsItems, selectApprovalsSearch],
    (items: ChecklistItem[], query: string) => {
        const q = query.trim().toLowerCase();
        const matches = items.filter((item) => item.title.trim().toLowerCase().includes(q));
        return matches;
    },
);

export const selectSearchedUsers = createSelector(
    [selectOrgUsers, selectUsersSearch],
    (users: LiteOrgUser[], query: string) => {
        const q = query.toLowerCase().replace(/\s/g, ''); //remove spaces

        const emailMatches = users.filter((user) => user.email?.trim().toLowerCase().includes(q));
        const fullNameMatches = users.filter((user) =>
            `${user.firstName?.trim().toLowerCase()}${user.lastName?.trim().toLowerCase()}`.includes(q),
        );
        return unionBy(emailMatches, fullNameMatches, 'id');
    },
);

export const selectAveragedReleaseStatistics = createSelector(
    [selectReleaseStatsHistory, selectRelease],
    (releaseStatsHistory, release) => {
        const releaseStatsByTypes: Partial<Record<StatisticType, StatisticWithAssociatedInfo[]>> = {};
        releaseStatsByTypes[StatisticType.CrashFreeSessions] = [];
        releaseStatsByTypes[StatisticType.CrashFreeUsers] = [];
        releaseStatsByTypes[StatisticType.BetaCrashFreeSessions] = [];
        releaseStatsByTypes[StatisticType.BetaCrashFreeUsers] = [];

        const currentReleaseStatByExtendedType: Partial<Record<ExtendedStatisticType, StatisticWithAssociatedInfo>> =
            {};

        const allPastStatistics: StatisticWithAssociatedInfo[] = [];

        releaseStatsHistory.forEach((releaseStats) => {
            const allStatisticsOnRelease: StatisticWithAssociatedInfo[] = [];

            releaseStats.statistics.forEach((statistic) => {
                if (!releaseStatsByTypes[statistic.type]) {
                    releaseStatsByTypes[statistic.type] = [];
                }

                const statisticWithAssociatedInfo: StatisticWithAssociatedInfo = {
                    ...statistic,
                    releaseVersion: releaseStats.id,
                    completedAtOrReleasedAtTimestamp: releaseStats.completedAtOrReleasedAtTimestamp,
                };

                releaseStatsByTypes[statistic.type].push(statisticWithAssociatedInfo);
                allStatisticsOnRelease.push(statisticWithAssociatedInfo);

                if (releaseStats.id === release.version) {
                    const extendedType = extendedStatTypeForReleaseStatistic(statistic);
                    currentReleaseStatByExtendedType[extendedType] = statisticWithAssociatedInfo;
                }
            });

            const userStabilityStats: StatisticWithAssociatedInfo[] =
                releaseStats.allBuildsStabilityInfo?.map((build) => ({
                    type: StatisticType.CrashFreeSessions,
                    unit: StatisticUnit.Percent,
                    value: build.sessionStability,
                    average: null,
                    change: null,
                    changeStatus: null,
                    releaseVersion: releaseStats.id,
                    category: StatisticCategory.StabilityAndHealth,
                    completedAtOrReleasedAtTimestamp: releaseStats.completedAtOrReleasedAtTimestamp,
                })) ?? [];
            if (userStabilityStats.length > 0) {
                releaseStatsByTypes[StatisticType.CrashFreeSessions].push(userStabilityStats[0]);
                allStatisticsOnRelease.push(userStabilityStats[0]);
                if (releaseStats.id === release.version) {
                    currentReleaseStatByExtendedType[StatisticType.CrashFreeSessions] = userStabilityStats[0];
                }
            }

            const sessionStabilityStats: StatisticWithAssociatedInfo[] =
                releaseStats.allBuildsStabilityInfo?.map((build) => ({
                    type: StatisticType.CrashFreeUsers,
                    value: build.userStability,
                    unit: StatisticUnit.Percent,
                    average: null,
                    change: null,
                    changeStatus: null,
                    releaseVersion: releaseStats.id,
                    category: StatisticCategory.StabilityAndHealth,
                    completedAtOrReleasedAtTimestamp: releaseStats.completedAtOrReleasedAtTimestamp,
                })) ?? [];
            if (sessionStabilityStats.length > 0) {
                releaseStatsByTypes[StatisticType.CrashFreeUsers].push(sessionStabilityStats[0]);
                allStatisticsOnRelease.push(sessionStabilityStats[0]);
                if (releaseStats.id === release.version) {
                    currentReleaseStatByExtendedType[StatisticType.CrashFreeUsers] = sessionStabilityStats[0];
                }
            }

            const betaUserStabilityStats: StatisticWithAssociatedInfo[] =
                releaseStats.allBetaBuildsStabilityInfo?.map((build) => ({
                    type: StatisticType.BetaCrashFreeSessions,
                    unit: StatisticUnit.Percent,
                    value: build.sessionStability,
                    average: null,
                    change: null,
                    changeStatus: null,
                    releaseVersion: releaseStats.id,
                    category: StatisticCategory.StabilityAndHealth,
                    completedAtOrReleasedAtTimestamp: releaseStats.completedAtOrReleasedAtTimestamp,
                })) ?? [];
            if (betaUserStabilityStats.length > 0) {
                releaseStatsByTypes[StatisticType.BetaCrashFreeSessions].push(betaUserStabilityStats[0]);
                allStatisticsOnRelease.push(betaUserStabilityStats[0]);
                if (releaseStats.id === release.version) {
                    currentReleaseStatByExtendedType[StatisticType.BetaCrashFreeSessions] = betaUserStabilityStats[0];
                }
            }

            const betaSessionStabilityStats: StatisticWithAssociatedInfo[] =
                releaseStats.allBetaBuildsStabilityInfo?.map((build) => ({
                    type: StatisticType.BetaCrashFreeUsers,
                    value: build.userStability,
                    unit: StatisticUnit.Percent,
                    average: null,
                    change: null,
                    changeStatus: null,
                    releaseVersion: releaseStats.id,
                    category: StatisticCategory.StabilityAndHealth,
                    completedAtOrReleasedAtTimestamp: releaseStats.completedAtOrReleasedAtTimestamp,
                })) ?? [];

            if (betaSessionStabilityStats.length > 0) {
                releaseStatsByTypes[StatisticType.BetaCrashFreeUsers].push(betaSessionStabilityStats[0]);
                allStatisticsOnRelease.push(betaSessionStabilityStats[0]);
                if (releaseStats.id === release.version) {
                    currentReleaseStatByExtendedType[StatisticType.BetaCrashFreeUsers] = betaSessionStabilityStats[0];
                }
            }

            const currentReleaseReleasedAtTimestamp =
                release.releasedAt ? Math.trunc(new Date(release.releasedAt).getTime() / 1000) : Infinity;

            if (
                releaseStats.completedAtOrReleasedAtTimestamp &&
                releaseStats.completedAtOrReleasedAtTimestamp < currentReleaseReleasedAtTimestamp
            ) {
                // Should be accumulating statistics from all releases BEFORE the current release
                allPastStatistics.push(...allStatisticsOnRelease);
            }
        });

        const { calculatedStatistics } = calculateStatistics({ statistics: allPastStatistics });

        const currentReleaseStats: Statistic[] = Object.keys(currentReleaseStatByExtendedType).map((extendedType) => {
            const calculatedStatistic = calculatedStatistics.find((statistic) => {
                const extendedTypeForStatistic = extendedStatTypeForReleaseStatistic(statistic);
                return extendedTypeForStatistic === extendedType;
            });

            const currentReleaseStat = currentReleaseStatByExtendedType[extendedType];

            if (!calculatedStatistic) {
                return {
                    ...currentReleaseStat,
                    average: 0,
                    change: 0,
                    changeStatus: null,
                };
            }

            // These don't require averaging past stats in release overview, their changeStatus is calculated elsewhere
            if (
                calculatedStatistic.type === StatisticType.WaitingForReviewDuration ||
                calculatedStatistic.type === StatisticType.InReviewDuration ||
                calculatedStatistic.type === StatisticType.AppStoreBuildBundleDownloadSize ||
                calculatedStatistic.type === StatisticType.AppStoreBuildBundleInstallSize
            ) {
                return {
                    ...currentReleaseStat,
                };
            }

            const currentReleaseValue = currentReleaseStat?.value;

            if (
                isNil(currentReleaseValue) ||
                !calculatedStatistic ||
                isNil(calculatedStatistic.value) ||
                !calculatedStatistic.type
            ) {
                return null;
            }

            const change = Math.trunc(currentReleaseValue - calculatedStatistic.value); // statistic.value is an average of all past values

            return {
                ...calculatedStatistic,
                value: currentReleaseValue,
                average: calculatedStatistic.value,
                change,
                changeStatus: statusChangeForStatType({ type: calculatedStatistic.type, value: change }),
            };
        });

        return {
            releaseStatsByTypes,
            currentReleaseStats: compact(currentReleaseStats),
        };
    },
);

export const selectOrderedAppsInAppGroup = createSelector([selectAppsById], (apps) => {
    return [...apps].sort(sortAppsByPlatform);
});

export const selectAppHomeUpcomingReleases = createSelector([selectAppHome], (appHome) => {
    return appHome?.upcomingReleases ?
            [...appHome.upcomingReleases].sort((releaseA, releaseB) =>
                compareVersions(releaseA.version, releaseB.version),
            )
        :   undefined;
});

export const selectAppHomePastReleases = createSelector([selectAppHome], (appHome) => {
    return appHome?.pastReleases ?
            [...appHome.pastReleases].sort((releaseA, releaseB) => compareVersions(releaseA.version, releaseB.version))
        :   undefined;
});

export const selectUpcomingReleases = createSelector([selectReleasesForAppId], (releases) => {
    const { upcomingReleases } = groupReleasesByTimelinePhase(releases);

    return upcomingReleases;
});

export const selectCompletedReleases = createSelector([selectReleasesForAppId], (releases) => {
    const { completedReleases } = groupReleasesByTimelinePhase(releases);

    return completedReleases;
});

export const selectCurrentReleases = createSelector([selectReleasesForAppId], (releases) => {
    const { currentReleases } = groupReleasesByTimelinePhase(releases);

    return currentReleases;
});

// Integrations wired to no step can't sync, so they're excluded from both the refresh popover and
// the nav badge that summarises it — otherwise the badge flags an integration the popover won't show.
export const selectRefreshableAppIntegrations = createSelector([selectAppIntegrations], (integrations) =>
    integrations.filter((integration) => integration.wiredToStep !== false),
);

export const selectIntegrationRefreshStatusForIntegrations = createSelector(
    [selectRefreshableAppIntegrations],
    (integrations) => {
        return combinedStatusForIntegrationRefreshData(integrations);
    },
);

const UNLISTED_AUTOMATIONS = [AutomationType.AutoResignRollbackBuild];

export const selectSortedFilteredAutomations = createSelector([selectAppSettingAutomations], (automations) => {
    const sortedAutomations = [...automations].sort((a, b) => {
        if (a.isNew && b.isNew) {
            return a.name > b.name ? 1 : -1;
        } else if (a.isNew) {
            return -1;
        } else if (b.isNew) {
            return 1;
        }

        return 0;
    });

    return sortedAutomations.filter((automation) => !UNLISTED_AUTOMATIONS.includes(automation.type));
});

export const selectAutomationsByCategory = createSelector([selectSortedFilteredAutomations], (automations) => {
    return groupBy(automations, (automation) => categoryForAutomation(automation.type));
});

export const selectCommitsOnlyOnWorkingBranch = createSelector(
    [selectHasFeatureWorkItemsForStepRunId, selectAllWorkingBranchWorkItems, selectAllReleaseBranchWorkItems],
    (hasFeatureWorkItemsForRelease, workingBranchItems, releaseBranchItems) => {
        if (hasFeatureWorkItemsForRelease) {
            return getSortedCommitsOnlyOnWorkingBranch(workingBranchItems, releaseBranchItems);
        }

        return [];
    },
);

export const selectAllOpenPRs = createSelector(
    [selectAllOpenPRsAgainstWorkingBranch, selectAllOpenPRsAgainstReleaseBranch],
    (openPRsAgainstWorkingBranch, openPRsAgainstReleaseBranch) => {
        return getSortedOpenPRs(openPRsAgainstWorkingBranch, openPRsAgainstReleaseBranch);
    },
);

export const selectSortedGroups = createSelector([selectOrgGroups], (groups: Record<UserGroupId, UserGroup>) => {
    const defaultGroups: Record<UserGroupId, UserGroup> = {};
    const otherGroups: Record<UserGroupId, UserGroup> = {};
    const defaultGroupsIds = allCases(DefaultUserGroupId);

    for (const [id, group] of Object.entries(groups)) {
        if (defaultGroupsIds.includes(id as DefaultUserGroupId)) {
            defaultGroups[id] = group;
        } else {
            otherGroups[id] = group;
        }
    }

    const sortedDefaultGroups = Object.values(defaultGroups).sort((a, b) => {
        if (a.name < b.name) {
            return -1;
        }
        if (a.name > b.name) {
            return 1;
        }
        return 0;
    });

    const sortedOtherGroups = Object.values(otherGroups).sort((a, b) => {
        if (a.name < b.name) {
            return -1;
        }
        if (a.name > b.name) {
            return 1;
        }
        return 0;
    });

    return [...sortedDefaultGroups, ...sortedOtherGroups];
});

export const selectAssignableGroups = createSelector([selectSortedGroups], (groups: UserGroup[]) => {
    return groups.filter((group) => !group.disabled);
});

export const selectAppsInGroup = createSelector([selectAppsInAppGroup], (appsInAppGroup) => {
    return compact(appsInAppGroup);
});

export const selectAppOrgUsers = createSelector(
    [
        selectApp,
        (state: RootState) => selectOrgUsers(state),
        (state: RootState) => selectOrg(state)!.orgSettings.groups,
    ], // workaround for weird createSelector bug
    (app, orgUsers, orgGroups) => orgUsers.filter((user) => hasEffectiveAppAccess(user, app.id, orgGroups)),
);

export const selectOrgUsersWithGroup = createSelector(
    [(state: RootState) => selectOrgUsers(state), selectGroupMembers],
    (orgUsers, groupMembers) => {
        const orgUsersById = keyBy(orgUsers, (user) => user.id);
        const groupUsers: LiteOrgUser[] = compact(groupMembers.map((userId) => orgUsersById[userId]));
        return groupUsers;
    },
);
